import React, { useMemo } from 'react';

import {
  MembershipTextInput,
  RadioGroupInput,
} from '@components/admin/members/MembershipFormDetails/Forms';
import { Button } from '@components/Button';
import { SpinnerModal } from '@components/SpinnerModal';
import { usePatchCompany } from '@hooks/mutation/dashboard/usePatchCompany';
import { useSearch } from '@hooks/useSearch';
import { CompanyForm, MembershipFormFields } from '@models/MembershipForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import { Prompt } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    save: '保存',
    defaultTransactionConditions: '普通',
    proposedTransactionConditions: '相談したい',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  formContainer: {
    ...common.flexColumn,
    marginBottom: '1rem',
  },
  formButton: {
    width: 70,
    margin: '1.5rem 1rem',
  },
}));

const searchType = t.type({
  id: h.number().cast(),
});

interface CompanyInfoFormProps {
  initialValues: CompanyForm;
  onFinish: () => void;
}

export const CompanyFormik: React.FC<CompanyInfoFormProps> = (props) => {
  const { initialValues, onFinish } = props;

  const schema = useMemo(() => MembershipFormFields.shared.toCompanyJPFormSchema(), []);
  const validate = useMemo(() => createValidate(schema), [schema]);

  const { search } = useSearch(searchType);

  const patchCompany = usePatchCompany(search.id);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <Formik
      validate={validate}
      initialValues={initialValues}
      validateOnMount
      onSubmit={async (values) => {
        const validation = schema.decode(values);

        if (!isRight(validation)) {
          throw new Error('CompanyFormik: Unexpected erroneus form values. ');
        }

        const patchCompanyData = MembershipFormFields.shared.mapCompanyFormToCompany(
          validation.right,
        );

        try {
          await patchCompany.mutateAsync(patchCompanyData);
          onFinish();
        } catch (e) {
          if (e instanceof FormError) {
            // eslint-disable-next-line no-alert
            alert(e.getDisplayMessage());
          }
          throw e;
        }
      }}
    >
      {({ touched, isSubmitting }) => (
        <Form className={clsx(classes.formContainer, classes.verticalSpace1)}>
          <Prompt
            when={Object.keys(touched).length > 0 && !isSubmitting}
            message={() => formStrings.promptLeaving}
          />

          <SpinnerModal open={patchCompany.isLoading} />

          <MembershipTextInput field={MembershipFormFields.shared.companyName} />
          <MembershipTextInput field={MembershipFormFields.shared.englishName} />
          <MembershipTextInput field={MembershipFormFields.shared.corporateNumber} />
          <MembershipTextInput field={MembershipFormFields.shared.zipcode} />
          <MembershipTextInput field={MembershipFormFields.shared.address1} />
          <MembershipTextInput field={MembershipFormFields.shared.address2} />
          <MembershipTextInput field={MembershipFormFields.shared.phone} />
          <MembershipTextInput field={MembershipFormFields.shared.fax} />
          <MembershipTextInput field={MembershipFormFields.shared.startDate} />
          <MembershipTextInput field={MembershipFormFields.shared.representerLastName} />
          <MembershipTextInput field={MembershipFormFields.shared.representerFirstName} />
          <MembershipTextInput field={MembershipFormFields.shared.legalCapital} />
          <MembershipTextInput field={MembershipFormFields.shared.industry} />
          <MembershipTextInput field={MembershipFormFields.shared.employeeCount} />
          <RadioGroupInput
            name={MembershipFormFields.shared.transactionCondition.name}
            label={MembershipFormFields.shared.transactionCondition.label}
            options={[
              {
                label: strings.defaultTransactionConditions,
                value: 'Y',
              },
              {
                label: strings.proposedTransactionConditions,
                value: 'N',
              },
            ]}
          />
          <MembershipTextInput field={MembershipFormFields.shared.transactionConditionDetail} />
          <Button
            color="primary"
            className={classes.formButton}
            text={strings.save}
            shape="round"
          />
        </Form>
      )}
    </Formik>
  );
};
